import { memo } from 'react';
import type { CatalogFacets } from '@sbg/shared/schemas';
import { toggleFilterValue } from '@/lib/catalog';
import type { CatalogFilterState } from '@/lib/catalog';
import { refOptions } from './shop-utils';
import type { UpdateMode } from './useShopFilters';

export interface ColourStripProps {
  facets: CatalogFacets | undefined;
  filters: CatalogFilterState;
  onChange: (next: CatalogFilterState, mode?: UpdateMode) => void;
  /** The heading of the strip and the name of its group. */
  label: string;
}

/**
 * "Shop by colour" (D-379): the colour families as one row of round swatches under the title — the quickest way to a stone,
 * because a buyer thinks "black" or "red" before anything else. It is the same colour filter as the sidebar's (the same URL
 * parameter, the same toggle, several colours allowed), only closer to hand; a colour nothing matches is left out unless it is
 * already chosen.
 */
export const ColourStrip = memo(function ColourStrip({
  facets,
  filters,
  onChange,
  label,
}: ColourStripProps) {
  // Until the counts are in a chosen colour would only show its address word ("black"), so the row waits — holding its room
  // (so nothing below it moves when it arrives).
  if (!facets) return <div className="colour-strip colour-strip--pending" aria-hidden="true" />;
  const options = refOptions(facets, filters, 'color').filter(
    (option) => option.count > 0 || option.selected,
  );
  if (options.length === 0) return null;
  return (
    <div className="colour-strip" role="group" aria-label={label}>
      <span className="colour-strip__label" aria-hidden="true">
        {label}
      </span>
      <ul className="colour-strip__list">
        {options.map((option) => (
          <li key={option.value}>
            <button
              type="button"
              className="colour-strip__item"
              aria-pressed={option.selected}
              onClick={() => {
                onChange(toggleFilterValue(filters, 'color', option.value));
              }}
            >
              <span
                className="colour-strip__swatch"
                aria-hidden="true"
                {...(option.swatchHex ? { style: { backgroundColor: option.swatchHex } } : {})}
              />
              {option.label}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
});
